import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
} from '@tanstack/react-table';
import {
    deleteChannelPartner,
    getChannelPartners,
    updateChannelPartnerStatus,
} from '../store/channelPartners/actions';
import { capitalizeFirst } from '../commonUtils/string.helper';
import { hasPermission } from '../commonUtils/permission.helper';
import constants from '../commonUtils/constants';
import { Switch } from "../components/ui/switch";
import IconButton from '../components/IconButton';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';

const ChannelPartnersContext = createContext();

export const useChannelPartners = () => useContext(ChannelPartnersContext);

export const ChannelPartnersProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { filters, ChannelPartnersList, loading, errors } = useSelector((state) => state.ChannelPartners);

    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [toDeleteId, setToDeleteId] = useState("");
    const [updateData, setUpdateData] = useState({ _id: "", title: "" });

    const debouncedGetChannelPartners = useMemo(() => debounce((params) => {
        dispatch(getChannelPartners(params));
    }, 300), [dispatch]);

    useEffect(() => {
        debouncedGetChannelPartners({
            currentPage: 1,
            perPage: ChannelPartnersList?.pagination?.perPage || constants.tableUI.defaultPerpage,
            ...filters,
        });
    }, [filters, ChannelPartnersList?.pagination?.perPage, debouncedGetChannelPartners]);

    useEffect(() => {
        handleCloseModal('update');
        handleCloseModal('delete');
    }, [ChannelPartnersList]);

    const channelPartners = useMemo(() => ChannelPartnersList?.data?.map(
        ({ _id, key, title, status }, index) => ({
            id: index + 1,
            _id,
            key,
            title,
            status: capitalizeFirst(status),
        })
    ), [ChannelPartnersList]);

    const pagination = useMemo(() => ChannelPartnersList?.pagination, [ChannelPartnersList]);
        
    const handlePageChange = useCallback((currentPage, perPage) => {
        debouncedGetChannelPartners({ currentPage, perPage, ...filters });
    }, [debouncedGetChannelPartners, filters]);

    const editChannelPartner = useCallback((data) => setUpdateData(data), []);
    const handleToggleStatus = useCallback((id) => dispatch(updateChannelPartnerStatus(id)), [dispatch]);
    const askDeleteConfirmation = useCallback((id) => {
        setToDeleteId(id);
        setShowDeleteConfirmationDialog(true);
    }, []);

    const columnHelper = useMemo(() => createColumnHelper(), []);
    const columns = useMemo(() => [
        columnHelper.accessor('id', { header: '#', cell: info => info.getValue() }),
        columnHelper.accessor('key', { header: 'Key', cell: info => info.getValue() }),
        columnHelper.accessor('title', { header: 'Title', cell: info => info.getValue() }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: info => (
                <Switch
                    checked={info.getValue() === 'Active'}
                    onCheckedChange={() => handleToggleStatus(info.row.original._id)}
                    disabled={!hasPermission('channelPartners.update')}
                />
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            cell: info => (
                <>
                    {hasPermission('channelPartners.update') && (
                        <IconButton variant="action_default" size="icon" Icon={PencilSquareIcon} onClick={() => editChannelPartner(info.row.original)} />
                    )}
                    {hasPermission('channelPartners.create_delete') && (
                        <IconButton variant="action_default" size="icon" Icon={TrashIcon} onClick={() => askDeleteConfirmation(info.row.original._id)} />
                    )}
                </>
            ),
        }),
    ], [columnHelper, handleToggleStatus, editChannelPartner, askDeleteConfirmation]);

    const table = useReactTable({
        data: channelPartners,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageIndex: ChannelPartnersList?.pagination?.currentPage - 1,
                pageSize: ChannelPartnersList?.pagination?.perPage,
            },
        },
        manualPagination: true,
        pageCount: ChannelPartnersList?.pagination?.totalPages,
    });

    const handleCloseModal = (action) => {
        if(action === "update"){
            setUpdateData({id:"", title:""})
            setOpenUpdateModal(false);
        } else if(action === "delete") {
            setToDeleteId("");
            setShowDeleteConfirmationDialog(false);
        }
    }
    const handleDelete = () => {
        dispatch(deleteChannelPartner(toDeleteId));
        handleCloseModal('delete');
    };

    return (
        <ChannelPartnersContext.Provider value={{
            channelPartners,
            table,
            columns,
            loading,
            errors,
            pagination,
            handlePageChange,
            editChannelPartner,
            openUpdateModal,
            setOpenUpdateModal,
            updateData,
            handleCloseModal,
            showDeleteConfirmationDialog,
            askDeleteConfirmation,
            handleDelete,
        }}>
            {children}
        </ChannelPartnersContext.Provider>
    );
};
